import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './UploadImagesScreen.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages } from '@fortawesome/free-solid-svg-icons'
import axios from "axios";
import { useNavigate } from "react-router";
import LoadingOverlay from '../common/LoadingOverlay/LoadingOverlay';
import { useParams } from "react-router-dom";
import { useUser } from '../../context/UserContext';
import { getImageLimit, formatImageLimit } from '../../utils/imageLimit';
import { getAuthToken } from '../../utils/authStorage';

import { buildRegisterRequest } from "../../utils/imageMetadata";

const LEGACY_UPLOAD_BATCH_SIZE = 1;

const isSameFile = (fileA, fileB) =>
  fileA.name === fileB.name &&
  fileA.size === fileB.size &&
  fileA.lastModified === fileB.lastModified;

const splitIntoBatches = (files, batchSize) => {
  const batches = [];
  for (let index = 0; index < files.length; index += batchSize) {
    batches.push(files.slice(index, index + batchSize));
  }
  return batches;
};

const buildSignedUrlRequest = (files) => ({
  files: files.map((file) => ({
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
  })),
});


function UploadImagesScreen() {
  const [selectedFiles, setSelectedFiles] = useState([]); // Files accepted by the backend
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const location = useLocation();
  const { inspectionId: inspectionIdParam } = useParams();
  const {
    inspectionId: inspectionIdState,
    inspectionName,
    projectId,
    projectName,
    oldImages,
    createdBy,
    createdAt,
    kmzFile,
    inspectionDomain,
    imageModality,
  } = location.state || {};
  const inspectionId = inspectionIdState || inspectionIdParam;
  const [images, setImages] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0); // tracks in-flight uploads
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadLimitError, setUploadLimitError] = useState('');
  const [hasUploadFailure, setHasUploadFailure] = useState(false);
  const pendingUploads = useRef(0);
  const { user, loading: userLoading, isViewer } = useUser();
  const imageLimit = getImageLimit(user);
  const uploadedCount = images.length + selectedFiles.length;
  const reservedUploadCount = uploadedCount + uploadingFiles.length;
  const remainingUploadSlots = imageLimit == null
    ? Infinity
    : Math.max(imageLimit - reservedUploadCount, 0);
  const uploadCounterText = imageLimit == null
    ? `${selectedFiles.length} Photos uploaded`
    : `${uploadedCount}/${imageLimit} Photos uploaded`;
  const isUploading = uploadingCount > 0;
  const uploadProgressText = uploadProgress.total > 0
    ? `Uploading ${Math.min(uploadProgress.current, uploadProgress.total)} of ${uploadProgress.total}...`
    : "Uploading...";
  const hasUploadedImages = selectedFiles.length > 0;

  const navigate = useNavigate();

  useEffect(() => {
    if (isViewer) return;
    const goToImageSelection = () => {
      navigate(`/inspections/${inspectionId}/selectImages`, {
        replace: true,
        state: {
          inspectionId,
          inspectionName,
          projectId,
          projectName,
          createdBy,
          createdAt,
          kmzFile,
          inspectionDomain,
          imageModality,
        },
      });
    };

    if (Array.isArray(oldImages) && oldImages.length > 0) {
      goToImageSelection();
      return;
    }
    if (!inspectionId) return;

    const fetchImages = async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`/api/inspections/${inspectionId}/images`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const existingImages = Array.isArray(res.data?.images) ? res.data.images : [];
        if (existingImages.length > 0) {
          goToImageSelection();
          return;
        }
        setImages([]);
      } catch (error) {
        console.error("Error loading inspection images:", error);
      }
    };

    fetchImages();
  }, [
    inspectionId,
    oldImages,
    navigate,
    inspectionName,
    projectId,
    projectName,
    createdBy,
    createdAt,
    kmzFile,
    inspectionDomain,
    imageModality,
    isViewer,
  ]);

  // console.log("AddInspection:ProjectId:" + projectId)
  //   useEffect(() => {
  //     const token = localStorage.getItem("token");
  //     const fetchInspection = async () => {
  //       const res = await axios.get(`/api/inspections/${inspectionId}`, {
  //         headers: { Authorization: `Bearer ${token}` }
  //       });
  //       setInspection(res.data);
  //       setImages(Array.isArray(res.data.images) ? res.data.images : []); // si vienen anidadas
  //       console.log("data images");
  //       console.log(res.data.images);
  //     };
  //     fetchInspection();
  //   }, [inspectionId]);

  const queueFilesForUpload = (files) => {
    if (userLoading) {
      setUploadLimitError("Loading your image limit. Please try again in a moment.");
      return;
    }

    const knownFiles = [...selectedFiles, ...uploadingFiles];
    const uniqueFiles = files.filter(newFile => !knownFiles.some(existingFile => isSameFile(existingFile, newFile)));

    if (uniqueFiles.length === 0) return;

    if (remainingUploadSlots <= 0) {
      setUploadLimitError(`You reached your upload limit. ${formatImageLimit(imageLimit)}.`);
      setHasUploadFailure(false);
      return;
    }

    const acceptedFiles = imageLimit == null
      ? uniqueFiles
      : uniqueFiles.slice(0, remainingUploadSlots);

    if (acceptedFiles.length < uniqueFiles.length) {
      setUploadLimitError(`Only ${acceptedFiles.length} more image${acceptedFiles.length === 1 ? "" : "s"} can be uploaded. ${formatImageLimit(imageLimit)}.`);
      setHasUploadFailure(false);
    } else {
      setUploadLimitError('');
      setHasUploadFailure(false);
    }

    if (acceptedFiles.length > 0) {
      handleUpload(acceptedFiles);
    }
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files || []);
    queueFilesForUpload(newFiles);
    event.target.value = '';
  };

  const handleUpload = async (files) => {
    pendingUploads.current += files.length;
    setUploadingCount((c) => c + files.length);
    setUploadProgress((progress) => {
      const shouldStartFresh = progress.current >= progress.total;
      return shouldStartFresh
        ? { current: files.length > 0 ? 1 : 0, total: files.length }
        : { current: progress.current || 1, total: progress.total + files.length };
    });
    setUploadingFiles((prevFiles) => [...prevFiles, ...files]);
    const uploadedFiles = [];

    const showUploadingFile = (fileIndex) => {
      setUploadProgress((progress) => ({
        current: Math.min(fileIndex + 1, progress.total),
        total: progress.total,
      }));
    };

    const markUploadedFile = () => {
      setUploadProgress((progress) => ({
        current: Math.min(progress.current + 1, progress.total),
        total: progress.total,
      }));
    };

    const markFilesFinished = (finishedFiles) => {
      setUploadingCount((c) => Math.max(0, c - finishedFiles.length));
      setUploadingFiles((prevFiles) =>
        prevFiles.filter(
          (existingFile) =>
            !finishedFiles.some((finishedFile) => isSameFile(finishedFile, existingFile)),
        ),
      );
    };

    const uploadWithSignedUrls = async (token) => {
      const signedUrlResponse = await axios.post(
        `/api/uploads/inspections/${inspectionId}/signed-urls`,
        buildSignedUrlRequest(files),
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        },
      );

      const signedItems = signedUrlResponse.data?.items || [];
      if (signedItems.length !== files.length) {
        throw new Error("Could not prepare all images for upload.");
      }

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const signedItem = signedItems[index];
        showUploadingFile(index);

        await axios.put(signedItem.signedUrl, file, {
          headers: {
            "Content-Type": signedItem.contentType || file.type || "application/octet-stream",
          },
          timeout: 120000,
        });

        uploadedFiles.push(file);
        markUploadedFile();
      }

      await axios.post(
        `/api/inspections/${inspectionId}/images/register`,
        await buildRegisterRequest(files, signedItems),
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        },
      );

      setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
      markFilesFinished(files);
    };

    const uploadThroughBackend = async (token) => {
      const batches = splitIntoBatches(files, LEGACY_UPLOAD_BATCH_SIZE);

      for (const batch of batches) {
        const formData = new FormData();
        showUploadingFile(uploadedFiles.length);
        batch.forEach((file) => formData.append("files", file));

        await axios.post(`/api/inspections/${inspectionId}/upload-images`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 120000,
        });

        uploadedFiles.push(...batch);
        markUploadedFile();
        setSelectedFiles((prevFiles) => [...prevFiles, ...batch]);
        markFilesFinished(batch);
      }
    };

    try {
      const token = getAuthToken();

      try {
        await uploadWithSignedUrls(token);
      } catch (signedUrlError) {
        console.warn("Direct upload failed, falling back to backend upload:", signedUrlError);
        uploadedFiles.length = 0;
        await uploadThroughBackend(token);
      }

      setUploadLimitError('');
      setHasUploadFailure(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      const remainingFiles = files.filter(
        (file) => !uploadedFiles.some((uploadedFile) => isSameFile(file, uploadedFile)),
      );
      const serverMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data ||
        "Could not upload the images.";

      setUploadLimitError(
        uploadedFiles.length > 0
          ? `Uploaded ${uploadedFiles.length} of ${files.length} images. Try uploading the remaining images again.`
          : serverMessage,
      );
      setHasUploadFailure(false);
      markFilesFinished(remainingFiles);
    } finally {
      pendingUploads.current -= files.length;
      if (pendingUploads.current <= 0) {
        setUploadProgress((progress) => ({ current: progress.total, total: progress.total }));
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const dropped = Array.from(event.dataTransfer.files || []);

    queueFilesForUpload(dropped);
  };

  const handleDragOver = (event) => {
    event.preventDefault(); // Prevent default behavior to allow drop
  };

  if (isViewer) {
    return (
      <div className="uis-container">
        <h2>Upload unavailable</h2>
        <p>This viewer profile can only open inspections that already have results.</p>
        <button className="uis-save-button" type="button" onClick={() => navigate("/projects")}>
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="uis-page">
      <div className="uis-card">
        <header className="uis-header">
          <h2 className="uis-title">Add your project</h2>
          <p className="uis-subtitle">
            Follow the steps below and fill the required fields
          </p>
        </header>

        <form className="uis-form" onSubmit={(e) => e.preventDefault()}>
          <div className="uis-field">
            <label>
              Inspection Title <span>*</span>
            </label>
            <input type="text" value={inspectionName || ""} readOnly />
          </div>

          <div className="uis-field">
            <label>
              Created By <span>*</span>
            </label>
            <input type="text" value={createdBy || ""} readOnly />
          </div>

          <div className="uis-field">
            <label>
              Date <span>*</span>
            </label>
            <input type="text" value={createdAt || ""} readOnly />
          </div>

          <div className="uis-field">
            <label>
              Add Photos <span>*</span>
            </label>
            {/* Estado upload */}
            {isUploading && (
              <div className="uis-upload-status">
                {uploadProgressText}
              </div>
            )}
            {!hasUploadedImages || isUploading ? (
              <div
                className="uis-dropzone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  id="fileInput"
                  disabled={userLoading || remainingUploadSlots <= 0}
                  style={{ display: "none" }}
                />

                <label htmlFor="fileInput" className="uis-dropzone-inner">
                  <div className="uis-circle">
                    <FontAwesomeIcon icon={faImages} size="lg" />
                  </div>

                  <p className="uis-drop-title">Upload Photos</p>
                  <p className="uis-drop-help">
                    Drag and drop your photos here, or{" "}
                    <span className="uis-link">browse files</span>
                  </p>

                  <p className="uis-counter">
                    {uploadCounterText}
                  </p>
                  {uploadLimitError && (
                    <p className="uis-limit-error">{uploadLimitError}</p>
                  )}
                </label>
              </div>
            ) : (
              <div className="uis-upload-complete">
                <div className="uis-upload-complete-icon">
                  <FontAwesomeIcon icon={faImages} />
                </div>
                <div>
                  <h3>
                    {selectedFiles.length} image{selectedFiles.length === 1 ? "" : "s"} uploaded
                  </h3>
                  <p>Your images are ready. Select Save Now to continue.</p>
                  {uploadLimitError && (
                    <p className="uis-limit-error">{uploadLimitError}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="uis-actions">
            <button
              type="button"
              className="uis-btn"
              disabled={uploadingCount > 0 || hasUploadFailure}
              onClick={() => {
                if (hasUploadFailure) return;
                if (hasUploadedImages) {
                  navigate(`/inspections/${inspectionId}/selectImages`, {
                    state: {
                      inspectionId,
                      inspectionName,
                      projectId,
                      projectName,
                      createdBy,
                      createdAt,
                      files: selectedFiles,
                      kmzFile: kmzFile,
                      inspectionDomain,
                      imageModality,
                    }
                  });
                } else {
                  alert('Por favor, sube al menos una imagen.');
                }
              }}
            >
              {uploadingCount > 0 ? "Uploading images..." : "Save Now"}
            </button>
          </div>
        </form>
      </div>

      <LoadingOverlay
        show={uploadingCount > 0}
        text={uploadProgressText}
      />
    </div>
  );
}

export default UploadImagesScreen;
